import React, { memo, useEffect, useMemo } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Button } from 'primereact/button'

import Text from '../../../ui/Text'
import { WidgetLayout } from '../../../lib/widget-layout'
import {
    useCompanyGetCompanyByIdQuery,
    useCompanyUpdateCompanyMutation,
    type Company,
    type Partner,
} from '../../redux/api/web-api'
import { notifyError, notifySuccess } from '../notificationsManager/Notify'
import Input from '../../components/Input'
import TextArea from '../../components/TextArea'
import useText from '../../hooks/useText'
import { RootState } from '../../../admin/redux/store'
import { Dropdown } from '../../uikit/Dropdown'
import { useLocalizationContext } from '../../components/localization-provider'
import { getNamesWithFallback } from '../../../i18n/countries'
import {
    MAX_NAME_LENGTH,
    MAX_ORG_EMAIL_LENGTH,
    MAX_ORG_DESC_LENGTH,
    MAX_ORG_PHONE_NO_LENGTH,
    MAX_ORG_CONTACT_PERSON_LENGTH,
    MAX_ORG_ADDRESS_1_LENGTH,
    MAX_ORG_ADDRESS_2_LENGTH,
    MAX_ORG_COMMENT_LENGTH,
} from '../../constants/InputsLength'
import { validatePhoneNumber } from '../../utils/form-validation/field-format-validation'

interface IInputs {
    name: string
    phone: string
    contactPerson: string
    kWhPrice: number
    comment: string
    description: string
    email: string
    address1: string
    address2: string
    ownerReadOnly: boolean
    country: string | null
}

const getFormValues = (company: Company | undefined): IInputs => {
    return {
        name: company?.name ?? '',
        phone: company?.phone ?? '',
        contactPerson: company?.contactPerson ?? '',
        address1: company?.address1 ?? '',
        address2: company?.address2 ?? '',
        kWhPrice: company?.kWhPrice ?? 0,
        comment: company?.comment ?? '',
        description: company?.description ?? '',
        ownerReadOnly: company?.ownerReadOnly ?? false,
        email: company?.email ?? '',
        country: company?.country || null,
    }
}

export type OrganisationInformationWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    partner?: Partner
    isPartnerFetching: boolean
    disabled?: boolean
}

export const OrganisationInformationWidget = memo(function OrganisationInformationWidget({
    className,
    style,
    partner,
    isPartnerFetching,
    disabled,
}: OrganisationInformationWidgetProps): React.ReactElement {
    const text = useText()
    const { locale } = useLocalizationContext()
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const countryOptions = useMemo(
        () =>
            Object.entries(getNamesWithFallback(locale))
                .map(([key, value]) => ({ key, value }))
                .sort((a, b) => a.value.localeCompare(b.value, locale)),
        [locale]
    )

    const [updateCompany, { isLoading: isSaving }] = useCompanyUpdateCompanyMutation()
    const { data: company, isLoading: isLoading, refetch } = useCompanyGetCompanyByIdQuery({ companyId: companyId })

    const {
        register,
        control,
        handleSubmit,
        reset,
        formState: { errors, isValid, isDirty },
    } = useForm<IInputs>({
        defaultValues: getFormValues(company),
        mode: 'onChange',
        delayError: 500,
    })

    useEffect(() => {
        reset(getFormValues(company))
    }, [company, reset])

    const saveCompany = async (formValues: IInputs) => {
        if (disabled) return

        try {
            await updateCompany({
                company: {
                    ...company,
                    ...formValues,
                },
            }).unwrap()
            refetch()
            notifySuccess(text('msgCompanyUpdated'))
        } catch {
            notifyError(text('msgCompanyNotUpdated'))
        }
    }

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            title={text('lblOrganisationInformationWidgetTitle')}
            loadingStatus={isLoading}
            data-testid="org-settings-widget"
        >
            <form className="flex flex-wrap gap-5" noValidate>
                <section className="flex flex-column gap-3 flex-grow-1">
                    <div>
                        <label htmlFor="name" data-testid="name">
                            <Text code="lblName" />
                        </label>
                        <Input
                            data-testid="name-input"
                            id="name"
                            {...register('name', {
                                required: {
                                    value: true,
                                    message: text('msgNameRequired'),
                                },
                            })}
                            error={errors && errors.name?.message}
                            maxLength={MAX_NAME_LENGTH}
                            required
                            readOnly={disabled}
                        />
                    </div>
                    <div>
                        <label htmlFor="phone" data-testid="phone">
                            <Text code="lblPhone" />
                        </label>
                        <Input
                            id="phone"
                            {...register('phone', {
                                validate: (value) => validatePhoneNumber(value) || text('lblPhoneValueInvalid'),
                            })}
                            error={errors.phone?.message}
                            maxLength={MAX_ORG_PHONE_NO_LENGTH}
                            readOnly={disabled}
                        />
                    </div>
                    <div>
                        <label htmlFor="contactPerson" data-testid="contactPerson">
                            <Text code="lblContactPerson" />
                        </label>
                        <Input
                            id="contactPerson"
                            {...register('contactPerson')}
                            maxLength={MAX_ORG_CONTACT_PERSON_LENGTH}
                            readOnly={disabled}
                        />
                    </div>
                    <div>
                        <label htmlFor="price" data-testid="kWhPrice">
                            <Text code="lblPricePerKWhField" />
                        </label>
                        <Input
                            id="price"
                            type="number"
                            min="0"
                            {...register('kWhPrice', {
                                setValueAs: (v) => (v === '' ? undefined : +v),
                                min: {
                                    value: 0,
                                    message: text('lblKWhPriceNegative'),
                                },
                            })}
                            error={errors && errors.kWhPrice?.message}
                            readOnly={disabled}
                        />
                    </div>
                    <div>
                        <label htmlFor="comment" data-testid="comment">
                            <Text code="lblComment" />
                        </label>
                        <TextArea
                            id="comment"
                            rows={5}
                            cols={5}
                            {...register('comment')}
                            maxLength={MAX_ORG_COMMENT_LENGTH}
                            readOnly={disabled}
                        />
                    </div>
                </section>
                <section className="flex flex-column gap-3 flex-grow-1">
                    <div>
                        <h3 className="mt-0 mb-2 text-lg">{text('lblOrganisationPartnerLabel')}</h3>
                        <p className="m-0" data-testid="partner">
                            {isPartnerFetching
                                ? text('msgLoading')
                                : partner?.name || text('lblServicePartnerUnknownPartner')}
                        </p>
                    </div>
                    <div>
                        <label htmlFor="description" data-testid="description">
                            <Text code="lblDescriptionField" />
                        </label>
                        <Input
                            id="description"
                            data-testid="description-input"
                            {...register('description')}
                            error={errors && errors.description?.message}
                            maxLength={MAX_ORG_DESC_LENGTH}
                            readOnly={disabled}
                        />
                    </div>
                    <div>
                        <label htmlFor="email" data-testid="email">
                            <Text code="lblEmail" />
                        </label>
                        <Input
                            type="email"
                            id="email"
                            {...register('email')}
                            maxLength={MAX_ORG_EMAIL_LENGTH}
                            readOnly={disabled}
                        />
                    </div>
                    <div>
                        <label htmlFor="address1" data-testid="address1">
                            <Text code="lblAddressLineOneField" />
                        </label>
                        <Input
                            id="address1"
                            {...register('address1')}
                            maxLength={MAX_ORG_ADDRESS_1_LENGTH}
                            readOnly={disabled}
                        />
                    </div>
                    <div>
                        <label htmlFor="address2" data-testid="address2">
                            <Text code="lblAddressLineTwoField" />
                        </label>
                        <Input
                            id="address2"
                            {...register('address2')}
                            maxLength={MAX_ORG_ADDRESS_2_LENGTH}
                            readOnly={disabled}
                        />
                    </div>
                    <div>
                        <label htmlFor="country" data-testid="country-label">
                            <Text code="lblCountry" />
                        </label>
                        <Controller
                            name="country"
                            control={control}
                            render={({ field }) => (
                                <Dropdown
                                    id="country"
                                    aria-label={text('lblCountry')}
                                    options={countryOptions}
                                    optionLabel="value"
                                    optionValue="key"
                                    value={field.value}
                                    disabled={disabled}
                                    onChange={(event) => field.onChange(event.value ?? null)}
                                    onBlur={field.onBlur}
                                />
                            )}
                        />
                    </div>
                    <div>
                        <h5>
                            <Text code="lblReadOnlyAccess" />
                        </h5>
                        <div>
                            <Text code="lblReadOnlyAccessDescription" />
                        </div>

                        <div className="flex align-items-center gap-1">
                            <input
                                id="ownerReadOnly"
                                type="checkbox"
                                className="checkbox-black"
                                disabled={disabled}
                                {...register('ownerReadOnly')}
                            />
                            <label className="mt-1 mb-0" htmlFor="ownerReadOnly" data-testid="ownerReadOnly">
                                <h5>{text('lblActivateReadOnlyAccess')}</h5>
                            </label>
                        </div>
                    </div>
                    {!disabled && (
                        <div className="flex justify-content-end mt-auto">
                            <Button
                                data-testid="save-btn"
                                type="submit"
                                disabled={isLoading || isSaving || !isValid || !isDirty}
                                onClick={handleSubmit(saveCompany)}
                                label={text('btnSave')}
                                icon="pi pi-save"
                            />
                        </div>
                    )}
                </section>
            </form>
        </WidgetLayout>
    )
})
